/**
 * CommunityCamRoom — the private/community Live entry point.
 *
 * As of the "unify all Live experiences" refactor, this page is a thin
 * wrapper around the canonical <LiveRoom> shell. All layout, chat, gifts,
 * reactions, viewers, A/V controls, and creator tools are shared with
 * Vault/regular Live via `mode="community"` capability gating.
 *
 * Privacy: membership + LiveKit token are still resolved via
 * /communities/:id/private-cam/token, so non-members remain redirected.
 * Manager-only kick is preserved through the shared LiveRoomStage's host
 * tile menu (community mode adds a "Remove" option).
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ChevronLeft, VideoOff } from "lucide-react";
import LiveRoom from "@/components/LiveRoom";

export default function CommunityCamRoom() {
  const { id } = useParams();
  // iOS shell: Cam Rooms are not available in the native app (server also
  // refuses to mint tokens — this redirect is UX polish).
  const nativeBlocked =
    window.Capacitor?.isNativePlatform?.() && window.Capacitor?.getPlatform?.() === "ios";
  const navigate = useNavigate();
  const [community, setCommunity] = useState(null);
  const [config, setConfig] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (nativeBlocked) return;
    let cancelled = false;
    (async () => {
      try {
        const [{ data: c }, tokenRes] = await Promise.all([
          api.get(`/communities/${id}`),
          api.post(`/communities/${id}/private-cam/token`),
        ]);
        if (cancelled) return;
        setCommunity(c);
        setConfig(tokenRes.data);
        toast("Reminder: nudity & sexually explicit content are prohibited — MyPandaX rules apply in private rooms.", { duration: 6000 });
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail || "Could not enter cam room.";
        setErr(detail);
        if (e?.response?.status === 403) {
          setTimeout(() => navigate(`/c/${id}`, { replace: true }), 1200);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate, nativeBlocked]);

  const kickParticipant = async (identity) => {
    if (!identity) return;
    if (!window.confirm(`Remove ${identity} from the Cam Room?`)) return;
    try {
      await api.post(`/communities/${id}/private-cam/kick/${encodeURIComponent(identity)}`);
      toast.success("Removed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove.");
    }
  };

  if (nativeBlocked) {
    return <Navigate to={`/c/${id}`} replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-zinc-400" data-testid="community-cam-loading">
        <Loader2 size={16} className="animate-spin mr-2" /> Connecting to the cam room…
      </div>
    );
  }
  if (err || !config || !community) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-center px-6" data-testid="community-cam-error">
        <VideoOff size={32} className="text-rose-400 mb-3" />
        <p className="text-sm text-zinc-300 max-w-md">{err || "Could not enter."}</p>
        <Link
          to={`/c/${id}`}
          className="mt-4 inline-flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200"
        >
          <ChevronLeft size={12} /> Back to community
        </Link>
      </div>
    );
  }

  // Build the LiveRoom-shaped stream doc so the shared shell reads it the
  // same way it reads a Vault/regular live stream. `stream_id` comes from
  // the Path α idempotent live_streams promotion — the same ID persists
  // across token refreshes/reconnects.
  const pseudoStream = {
    id: config.stream_id || community.id,
    title: `${community.name} — Live`,
    // Live-parity: the member who opened the room is the host (returned by
    // the token endpoint); everyone else joins as a viewer.
    host_id: config.host_id || community.owner_id || community.created_by,
    host: { handle: community.name, display_name: community.name },
    is_live: true,
    is_community: true,
    community_id: community.id,
    stage: { layout: "auto", pinned_id: null, spotlight_id: null },
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="community-cam-room">
      <LiveRoom
        stream={pseudoStream}
        mode="community"
        community={community}
        tokenEndpoint={`/communities/${id}/private-cam/token`}
        onKickParticipant={config.is_manager ? kickParticipant : undefined}
        onClose={() => navigate(`/c/${id}`)}
      />
    </div>
  );
}
